import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

const HeroStyles = {
  teal: '#255E5E',
  accentSurface: '#D6ECE4',
  textSecondary: '#3F4040',
  toggleTrackBg: '#2E6B6B',
};
export const StLocationSearchHero = styled(Box)(({ theme }) => {
  return {
    position: 'relative',
    minHeight: '485px',
    overflow: 'visible',
    backgroundColor: HeroStyles.teal,
    [theme.breakpoints.down('lg')]: {
      minHeight: 'auto',
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 'auto',
      background: 'linear-gradient(99.98deg, #013F3B 35.53%, #4AA585 98.51%)',
    },
  };
});

export const StHeroBackgroundImage = styled(Box)({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  pointerEvents: 'none',
  zIndex: 0,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
});

export const StHeroContent = styled(Box)(({ theme }) => {
  return {
    position: 'relative',
    zIndex: 1,
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '58px 87px 48px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    [theme.breakpoints.down('lg')]: {
      padding: '48px 24px',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '58px 27px 24px 32px',
      gap: '12px',
    },
  };
});

export const StHeroBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  alignSelf: 'flex-start',
  padding: '4px 12px',
  backgroundColor: 'rgba(255, 255, 255, 0.96)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '9999px',
  fontFamily: theme.typography.fontFamily,
  fontSize: '14px',
  lineHeight: '21px',
}));

export const StHeroHeadlineWrap = styled(Box)(({ theme }) => ({
  '& .MuiTypography-root': {
    [theme.breakpoints.down('md')]: {
      fontSize: '48px',
      fontWeight: 700,
      lineHeight: '53px',
      margin: 0,
      letterSpacing: '-0.48px',
    },
    '& > span> :where(p)': {
      margin: 0,
    },
  },
}));

export const StHeroSubtitle = styled(Box)(({ theme }) => ({
  margin: 0,
  maxWidth: '672px',
  fontFamily: theme.typography.fontFamily,
  '& > paragraph > span': {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 500,
    letterSpacing: '0.32px',
  },
}));

export const StSearchBar = styled(Box)(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '686px',
    maxWidth: '100%',
    padding: '4px 16px 4px 4px',
    backgroundColor: theme.palette.secondary.main,
    border: '1px solid #CFCFD0',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.0509804)',
    [theme.breakpoints.down('lg')]: {
      width: '100%',
      padding: '8px 24px',
      borderColor: '#E2E8F0',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      padding: '8px 12px',
      borderColor: '#E2E8F0',
    },
  };
});

export const StSearchBarMainRow = styled(Box)(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: '56px',
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      minHeight: '120px',
      gap: '8px',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      minHeight: '120px',
      gap: '8px',
    },
  };
});

export const StSearchInputRow = styled(Box)(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
    padding: '0 12px 0 16px',
    [theme.breakpoints.down('lg')]: {
      padding: 0,
      width: '100%',
      minHeight: '56px',
    },
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      width: '100%',
      minHeight: '56px',
    },
  };
});

export const StMapPinWrap = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  flexShrink: 0,
});

export const StLocationInput = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiInputBase-root': {
    fontFamily: theme.typography.fontFamily,
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 400,
    letterSpacing: '0.32px',
    color: HeroStyles.textSecondary,
  },
  '& .MuiInputBase-input::placeholder': {
    color: HeroStyles.textSecondary,
    opacity: 1,
  },
}));

export const StSearchActions = styled(Box)(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0,
    [theme.breakpoints.down('lg')]: {
      width: '100%',
      justifyContent: 'space-between',
      minHeight: '48px',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'space-between',
      minHeight: '48px',
      '& .MuiButton-root': {
        minHeight: '48px',
        height: '48px',
        padding: '4px 24px',
        borderRadius: '320px',
        backgroundColor: HeroStyles.teal,
        minWidth: '64px',
      },
    },
  };
});

export const StToggleRow = styled(Box)(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    [theme.breakpoints.down('lg')]: {
      gap: '6px',
    },
    [theme.breakpoints.down('sm')]: {
      gap: '6px',
    },
  };
});

export const StToggleTrack = styled('button', {
  shouldForwardProp: (prop) => prop !== '$checked',
})<{ $checked: boolean }>(({ $checked, theme }) => ({
  position: 'relative',
  width: '37.71px',
  height: '22px',
  padding: '1.57px',
  border: 'none',
  borderRadius: '784.929px',
  backgroundColor: $checked ? HeroStyles.toggleTrackBg : '#CFCFD0',
  cursor: 'pointer',
  flexShrink: 0,
  transition: 'background-color 0.2s ease',
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '1.57px',
    left: $checked ? '17.28px' : '1.57px',
    width: '18.86px',
    height: '18.86px',
    borderRadius: '50%',
    backgroundColor: theme.palette.secondary.main,
    transition: 'left 0.2s ease',
  },
}));

export const StToggleLabel = styled('label')(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '14px',
  lineHeight: '21px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
}));

export const StValidationMessage = styled(Box)({
  alignSelf: 'flex-start',
  backgroundColor: '#C61F1F',
  borderRadius: '6px',
  padding: '6px 12px 6px 10px',
  boxSizing: 'border-box',
});

export const StSearchBarWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  width: '686px',
  maxWidth: '100%',
  gap: '6px',
  [theme.breakpoints.down('lg')]: {
    width: '100%',
    gap: '16px',
  },
  [theme.breakpoints.down('sm')]: {
    gap: '12px',
  },
}));

export const StSuggestionsList = styled('ul')(({ theme }) => ({
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  width: '100%',
  boxSizing: 'border-box',
  zIndex: 20,
  margin: 0,
  padding: '4px 0',
  listStyle: 'none',
  backgroundColor: theme.palette.common.white,
  border: '1px solid #CFCFD0',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
  maxHeight: '280px',
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitOverflowScrolling: 'touch',
}));

export const StSuggestionItem = styled('li', {
  shouldForwardProp: (prop) => prop !== '$highlighted',
})<{ $highlighted?: boolean }>(({ $highlighted }) => ({
  margin: 0,
  padding: '10px 16px',
  cursor: 'pointer',
  fontSize: '14px',
  lineHeight: '20px',
  color: '#3F4040',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  backgroundColor: $highlighted ? '#D6ECE4' : 'transparent',
  '&:hover': {
    backgroundColor: '#D6ECE4',
  },
}));

export const StCommunitiesRow = styled(Box)(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    paddingTop: '16px',
    [theme.breakpoints.down('lg')]: {
      gap: '8px',
      paddingTop: 0,
    },
    [theme.breakpoints.down('sm')]: {
      gap: '6px',
      paddingTop: '16px',
      alignContent: 'center',
    },
  };
});

export const StCommunitiesLabel = styled('span')(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 500,
  color: '#D3E3DF',
}));

export const StCommunityPills = styled(Box)(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    [theme.breakpoints.down('lg')]: {
      gap: '8px',
    },
    [theme.breakpoints.down('sm')]: {
      gap: '6px',
      display: 'contents',
    },
  };
});

export const StCommunityPill = styled('button', {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected?: boolean }>(({ theme, $selected }) => {
  return {
    height: '40px',
    padding: '6px 16px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '320px',
    backgroundColor: $selected ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.1)',
    color: theme.palette.common.white,
    fontFamily: theme.typography.fontFamily,
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 500,
    cursor: 'pointer',
    [theme.breakpoints.down('lg')]: {
      height: '34px',
      fontWeight: 400,
    },
    [theme.breakpoints.down('sm')]: {
      height: '40px',
      borderRadius: '9999px',
      fontWeight: 400,
    },
    '&:hover': {
      backgroundColor: $selected ? 'rgba(255, 255, 255, 0.45)' : 'rgba(255, 255, 255, 0.2)',
    },
    '&:active': {
      backgroundColor: $selected ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.3)',
    },
    '&:focus-visible': {
      outline: '2px solid rgba(255, 255, 255, 0.8)',
      outlineOffset: '2px',
    },
  };
});
